import React, { memo } from "react";
import { Box } from "@mui/material";
import {
  BASE,
  BORDER,
  BRAND,
  GREY,
  PRIMARY,
} from "../../../../../utils/constants/colors";
import { fontWeight, ICON_LABELS } from "../../../../../utils/constants/UI";
import IconComponent from "../../../../../components/Icon/IconComponent";
import TypographyInline from "../../../../../components/Typography/TypographyInline";
import { Button } from "../../../../../components/Buttons";
import { sharedBillingStyles as s, formatCredits } from "./billingUtils";

const formatPrice = (price) => {
  if (price == null) return "$0";
  if (price < 1) return `$${price.toFixed(2)}`;
  return Number.isInteger(price) ? `$${price}` : `$${price.toFixed(2)}`;
};

const BillingBuyCreditsCard = memo(
  ({ remaining, topUps = [], buyingPlanId, onBuy }) => {
    const visibleTopUps = (topUps || []).filter((t) => t.credits > 0);
    const featuredIdx = visibleTopUps.length
      ? Math.floor((visibleTopUps.length - 1) / 2)
      : -1;

    const lowOnCredits =
      typeof remaining === "number" && remaining >= 0 && remaining < 1000;

    if (visibleTopUps.length === 0) return null;

    const colCount = Math.min(visibleTopUps.length, 5);

    return (
      <Box style={s.card}>
        <Box
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: "12px",
            marginBottom: "8px",
            flexWrap: "wrap",
          }}
        >
          <Box style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Box
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "10px",
                background: PRIMARY.W_50,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <IconComponent
                iconLabel={ICON_LABELS.MONETIZATION_ON}
                style={{ color: BRAND.PRIMARY, fontSize: "22px" }}
              />
            </Box>
            <Box>
              <TypographyInline
                size="md"
                fontWeight={fontWeight.SEMIBOLD}
                color={GREY.PRIMARY}
                label="Need more credits?"
              />
              <TypographyInline
                size="xs"
                color={GREY.SECONDARY}
                label="Top up instantly."
              />
              {/* T2: honest disclosure — top-ups sit on top of the plan but reset at the next credit
                  refresh (monthly renewal, or the monthly refill on annual plans). */}
              <Box style={{ marginTop: "4px" }}>
                <TypographyInline
                  size="micro"
                  color={GREY.TERTIARY}
                  label="Top-up credits are added on top of your plan and reset at your next credit refresh."
                />
              </Box>
            </Box>
          </Box>

          {lowOnCredits && (
            <span
              style={{
                ...s.badge,
                background: PRIMARY.W_50,
                color: BRAND.PRIMARY,
                whiteSpace: "nowrap",
              }}
            >
              {`${formatCredits(remaining)} LEFT`}
            </span>
          )}
        </Box>

        <Box
          style={{
            display: "grid",
            gap: "12px",
            marginTop: "20px",
          }}
          sx={{
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              md: `repeat(${Math.min(colCount, 3)}, minmax(0, 1fr))`,
              lg: `repeat(${colCount}, minmax(0, 1fr))`,
            },
          }}
        >
          {visibleTopUps.map((pack, idx) => {
            const featured = idx === featuredIdx;
            const isProcessing = buyingPlanId === pack.id;
            const isAnyBuying = !!buyingPlanId;
            const perCredit = pack.credits > 0 ? pack.price / pack.credits : 0;
            return (
              <Box
                key={pack.id}
                style={{
                  position: "relative",
                  padding: "18px 16px 16px",
                  border: `1px solid ${featured ? BRAND.PRIMARY : BORDER.DEFAULT}`,
                  borderRadius: "12px",
                  background: featured ? PRIMARY.W_50 : BASE.WHITE,
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                  transition:
                    "border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease",
                  boxShadow: featured
                    ? `0 10px 22px -18px ${BRAND.PRIMARY}`
                    : `0 6px 14px -16px ${GREY.SECONDARY}`,
                  minWidth: 0,
                }}
                sx={{
                  "&:hover": {
                    borderColor: BRAND.PRIMARY,
                    background: featured ? PRIMARY.W_50 : PRIMARY.W_50,
                    transform: "translateY(-1px)",
                  },
                }}
              >
                {featured && (
                  <Box
                    style={{
                      position: "absolute",
                      top: "-10px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      background: BRAND.PRIMARY,
                      color: BASE.WHITE,
                      padding: "3px 12px",
                      borderRadius: "999px",
                      fontSize: "10px",
                      fontWeight: 700,
                      letterSpacing: "0.4px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    POPULAR
                  </Box>
                )}

                <Box
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "2px",
                    textAlign: "center",
                  }}
                >
                  <Box
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      justifyContent: "center",
                      gap: "4px",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    <TypographyInline
                      size="xl"
                      fontWeight={fontWeight.BOLD}
                      color={GREY.PRIMARY}
                      label={formatCredits(pack.credits)}
                    />
                  </Box>
                  <TypographyInline
                    size="xs"
                    color={GREY.TERTIARY}
                    label="credits"
                  />
                </Box>

                <Box
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "2px",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  <TypographyInline
                    size="md"
                    fontWeight={fontWeight.BOLD}
                    color={BRAND.PRIMARY}
                    label={formatPrice(pack.price)}
                  />
                  {perCredit > 0 && (
                    <TypographyInline
                      size="xs"
                      color={GREY.TERTIARY}
                      label={`$${perCredit < 0.01 ? perCredit.toFixed(4) : perCredit.toFixed(2)} / credit`}
                    />
                  )}
                </Box>

                <Box style={{ marginTop: "auto" }}>
                  <Button
                    label={isProcessing ? "Processing..." : "Buy Now"}
                    size="xs28"
                    variant={featured ? "primary" : "ghost"}
                    fullWidth
                    disabled={isAnyBuying || !pack.stripe_price_id}
                    onClick={() => onBuy?.(pack)}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  },
);

BillingBuyCreditsCard.displayName = "BillingBuyCreditsCard";

export default BillingBuyCreditsCard;
